import { stringify } from 'qs';
import { AxiosError } from 'axios';
import IPagination from '../../../common/interfaces/IPagination';
import { getCarrierAxiosInstance } from '../../../common/services/axios-config';
import {
    ContactType,
    ICarriersContact,
} from '../../../common/interfaces/IContacts';
import { DEFAULT_CARRIER_LOGO } from '../constants/carriers.constants';
import ICarriersFiltersModel from '../interfaces/ICarriersFiltersModel';
import ICarriersRequest from '../interfaces/ICarriersRequest';
import ICarriersResponse from '../interfaces/ICarriersResponse';
import IUpdateCarrierContactRequest from '../interfaces/IUpdateCarrierContactRequest';
import ProcessingError, {
    getProcessingErrors,
    isProcessingError,
} from '../../../common/errors/processing-error';
import IApiError from '../../../common/errors/interfaces/IApiError';
import CONTACT_SUPPORT_MESSAGE from '../../../common/constants/messages';
import i18n from '../../../lib/i18n';

const setDefaultLogo = (carriers: ICarriersResponse[]) => {
    carriers.forEach(carrier => {
        carrier.logoUrl = carrier.logoUrl ?? DEFAULT_CARRIER_LOGO;
    });
};

const getCarriersList = async (
    page: number,
    pageSize: number,
    filters: ICarriersFiltersModel,
): Promise<IPagination<ICarriersResponse>> => {
    const request: ICarriersRequest = {
        filters,
        _page: page,
        _size: pageSize,
    };

    const Api = await getCarrierAxiosInstance();

    const response = await Api.get('v1/carriers:paginated', {
        params: request,
        paramsSerializer(params) {
            return stringify(params, { allowDots: true });
        },
    }).catch(() => {
        throw new Error(i18n.t('TransportadoraErroConsulta'));
    });

    if (response.data) {
        const { data } = response;
        setDefaultLogo(data.data);
        return data as IPagination<ICarriersResponse>;
    }

    return {} as IPagination<ICarriersResponse>;
};

const updateCarrierContact = async (
    carrierId: string,
    carrierContact: ICarriersContact,
): Promise<string> => {
    const api = await getCarrierAxiosInstance();

    const request: IUpdateCarrierContactRequest = {
        contactId: carrierContact.id,
        email: carrierContact.email,
        phone: carrierContact.phone,
        responsible: carrierContact.responsible,
        occupation: carrierContact.occupation,
        contactType: carrierContact.contactType ?? ContactType.operational,
        mainContact: true,
    } as IUpdateCarrierContactRequest;

    const response = await api
        .put(`v1/carriers/${carrierId}/contacts`, request)
        .catch(() => {
            throw new Error(i18n.t('TransportadoraErroTentarAtualizar'));
        });

    return response.data;
};

const deleteCarriers = async (
    carriersToDelete: ICarriersResponse[],
): Promise<any> => {
    const api = await getCarrierAxiosInstance();

    const carriersIdsToDelete: string[] = [];

    carriersToDelete.forEach(c => {
        carriersIdsToDelete.push(c.id);
    });

    const response = await api
        .post(`v1/carriers:delete`, carriersIdsToDelete)
        .catch((error: AxiosError<IApiError>) => {
            if (isProcessingError(error)) {
                const exceptionCarriersNames = getProcessingErrors(
                    error,
                    carriersToDelete.map(carrier => ({
                        id: carrier.id,
                        name: carrier.tradeName,
                    })),
                );

                if (exceptionCarriersNames.length > 0) {
                    const carriersNames = exceptionCarriersNames.join(', ');
                    const errorMessage = `${i18n.t('TransportadoraInicialMensagem')} ${carriersNames}. ${i18n.t(CONTACT_SUPPORT_MESSAGE)}`;

                    throw new ProcessingError(errorMessage);
                }
            }

            throw new Error(i18n.t(CONTACT_SUPPORT_MESSAGE));
        });

    return response.data;
};

const CarriersService = {
    getCarriersList,
    updateCarrierContact,
    deleteCarriers,
};

export default CarriersService;
